import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MinusIcon, PlusIcon } from '@heroicons/react/24/outline'

/**
 * VH-111: SessionQuantityPicker
 *
 * Amazon-style quantity picker for adding N copies of one or more
 * TrainingTemplates as ProgramSessions in a single submit.
 *
 * Dialog-agnostic: this component renders only the picker UI (list +
 * steppers + Add button). The parent decides whether to wrap it in a
 * Headless UI Dialog or inline it inside a form.
 *
 * Props:
 *  - templates: Array<{ id, name?, name_en?, color? }>
 *  - onSubmit: (quantityMap, onProgress) => Promise<void>
 *      quantityMap is { [template_id]: count } — only templates with count > 0.
 *      onProgress is (current, total) => void the parent calls to update
 *      our internal progress display during the add loop.
 *  - onCreateTemplate?: () => void
 *      Optional callback for the empty-state "Create your first template"
 *      button. If not provided, the empty state still shows but without
 *      the action button.
 *  - hideSubmitButton?: boolean
 *      When true, the picker renders only the stepper list (no Add button,
 *      no progress row). The parent owns the submit UX — useful when the
 *      picker is embedded inside a form whose own Create button drives the
 *      action. The parent reads the current state by listening to
 *      onQuantityChange.
 *  - onQuantityChange?: (quantityMap) => void
 *      Called whenever the quantity map changes (debounce-free). Lets the
 *      parent observe state without owning it.
 */
export function SessionQuantityPicker({
  templates = [],
  onSubmit,
  onCreateTemplate,
  hideSubmitButton = false,
  onQuantityChange,
}) {
  const { t } = useTranslation()

  // Internal quantity state per template. Initialized lazily to {} — each
  // template defaults to 0 (missing keys are treated as 0).
  const [quantities, setQuantities] = useState({})
  const [submitting, setSubmitting] = useState(false)
  const [progress, setProgress] = useState({ current: 0, total: 0 })

  const getCount = (templateId) => quantities[templateId] || 0

  const notifyChange = (next) => {
    if (onQuantityChange) {
      const map = {}
      for (const [tid, n] of Object.entries(next)) {
        if (n > 0) map[tid] = n
      }
      onQuantityChange(map)
    }
  }

  const increment = (templateId) => {
    setQuantities((prev) => {
      const next = { ...prev, [templateId]: (prev[templateId] || 0) + 1 }
      notifyChange(next)
      return next
    })
  }

  const decrement = (templateId) => {
    setQuantities((prev) => {
      const current = prev[templateId] || 0
      if (current <= 0) return prev
      const next = { ...prev, [templateId]: current - 1 }
      notifyChange(next)
      return next
    })
  }

  const totalCount = useMemo(
    () => Object.values(quantities).reduce((s, n) => s + (n || 0), 0),
    [quantities]
  )

  const canSubmit = totalCount >= 1 && !submitting

  const handleSubmit = async () => {
    if (!canSubmit) return
    setSubmitting(true)
    setProgress({ current: 0, total: totalCount })

    // Build clean quantity map (drop zeros).
    const map = {}
    for (const [tid, n] of Object.entries(quantities)) {
      if (n > 0) map[tid] = n
    }

    try {
      await onSubmit(map, (current, total) => {
        setProgress({ current, total })
      })
    } finally {
      setSubmitting(false)
      setProgress({ current: 0, total: 0 })
    }
  }

  // Empty state — no templates at all.
  if (!templates || templates.length === 0) {
    return (
      <div
        className="flex flex-col items-center justify-center py-12 px-6 text-center"
        data-testid="session-picker-empty"
      >
        <p className="text-gray-600 mb-4">{t('programs.sessionPicker.empty')}</p>
        {onCreateTemplate && (
          <button
            type="button"
            onClick={onCreateTemplate}
            className="btn-primary btn-sm"
            data-testid="session-picker-create-first"
          >
            {t('programs.sessionPicker.createFirst')}
          </button>
        )}
      </div>
    )
  }

  return (
    <div className="flex flex-col" data-testid="session-picker">
      <div className="max-h-96 overflow-y-auto divide-y divide-gray-200 border border-gray-200 rounded-md">
        {templates.map((tpl) => {
          const count = getCount(tpl.id)
          const name = tpl.name_en || tpl.name || `#${tpl.id}`
          return (
            <div
              key={tpl.id}
              className="flex items-center justify-between px-3 py-2"
              data-testid={`session-picker-row-${tpl.id}`}
            >
              <div className="flex items-center min-w-0 flex-1">
                {tpl.color && (
                  <span
                    className="w-3 h-3 rounded-full mr-2 flex-shrink-0"
                    style={{ backgroundColor: tpl.color }}
                  />
                )}
                <span className="text-sm text-gray-900 truncate">{name}</span>
              </div>
              <div className="flex items-center space-x-2 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => decrement(tpl.id)}
                  disabled={count === 0 || submitting}
                  aria-label={`decrement-${tpl.id}`}
                  data-testid={`session-picker-dec-${tpl.id}`}
                  className="w-8 h-8 flex items-center justify-center rounded border border-gray-300 bg-surface-container text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <MinusIcon className="h-4 w-4" />
                </button>
                <span
                  className="w-8 text-center font-mono text-base text-gray-900 tabular-nums"
                  data-testid={`session-picker-count-${tpl.id}`}
                >
                  {count}
                </span>
                <button
                  type="button"
                  onClick={() => increment(tpl.id)}
                  disabled={submitting}
                  aria-label={`increment-${tpl.id}`}
                  data-testid={`session-picker-inc-${tpl.id}`}
                  className="w-8 h-8 flex items-center justify-center rounded border border-gray-300 bg-surface-container text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <PlusIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          )
        })}
      </div>

      {!hideSubmitButton && (
        <div className="mt-4 flex items-center justify-between">
          <div className="text-sm text-gray-600" data-testid="session-picker-progress">
            {submitting && progress.total > 0 && (
              t('programs.sessionPicker.addingProgress', {
                current: progress.current,
                total: progress.total,
              })
            )}
          </div>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            data-testid="session-picker-submit"
            className="btn-primary btn-sm"
          >
            {t('programs.sessionPicker.add')}
            {totalCount > 0 && !submitting && ` (${totalCount})`}
          </button>
        </div>
      )}
    </div>
  )
}

export default SessionQuantityPicker
